import { useState } from "react";
import { phCapture } from "@/lib/posthog";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Search, ShieldX, MessageSquareQuote, Lock, BookOpen, ShoppingCart, Store, Megaphone } from "lucide-react";
import { buildOnboardingPreview, normalizeProductName } from "@/lib/buildOnboardingPreview";
import type { User } from "@supabase/supabase-js";

interface OnboardingModalProps {
  open: boolean;
  onClose: (product?: string) => void;
  user: User | null;
  onNavigateToAuth: () => void;
  isAdmin?: boolean;
}

const GOALS = [
  { id: "research", label: "Research before I sell", icon: BookOpen },
  { id: "amazon", label: "Improve my Amazon listing", icon: ShoppingCart },
  { id: "shopify", label: "Write a Shopify product page", icon: Store },
  { id: "ads", label: "Write ads & hooks", icon: Megaphone },
] as const;

const OnboardingModal = ({ open, onClose, user, onNavigateToAuth, isAdmin }: OnboardingModalProps) => {
  const [step, setStep] = useState(0);
  const [selectedGoal, setSelectedGoal] = useState<string | null>(null);
  const [productName, setProductName] = useState("");
  const [productError, setProductError] = useState("");
  const [goalError, setGoalError] = useState("");

  // Goal-specific preview cards via helper, with goal-aware fallback
  const rawPreview = (() => {
    try { return buildOnboardingPreview(productName, selectedGoal); } catch { return null; }
  })();

  const product = normalizeProductName(productName);
  const goal = selectedGoal || "research";

  const FALLBACK_TEMPLATES: Record<string, [string, string, string, string]> = {
    research: [
      `"Buyers often mention ${product} losing effectiveness over time."`,
      `"People hesitate because they can't tell if ${product} actually works."`,
      `"Finally found a ${product} that does what I expected."`,
      `"What 500+ buyers wish they'd known before buying ${product}."`,
    ],
    amazon: [
      `"Packaging and quality complaints keep coming up for ${product}."`,
      `"Shoppers question whether ${product} reviews are genuine."`,
      `"Matched the listing — exactly what I hoped ${product} would be."`,
      `"The #1 reason ${product} gets returned, straight from reviews."`,
    ],
    shopify: [
      `"Shipping delays and unclear tracking frustrate ${product} buyers."`,
      `"First-time visitors aren't sure they can trust this ${product} brand."`,
      `"Smooth experience — ${product} matched expectations."`,
      `"Trust-building messaging that converts more ${product} visitors."`,
    ],
    ads: [
      `"Buyers feel most ${product} ads overpromise on results."`,
      `"People scroll past because every ${product} ad looks the same."`,
      `"Saw the ad, tried ${product} — pleasantly surprised."`,
      `"A ${product} hook backed by what real buyers actually say."`,
    ],
  };

  const previewCards = Array.isArray(rawPreview) && rawPreview.length === 4
    ? rawPreview
    : (() => {
        const fb = FALLBACK_TEMPLATES[goal] || FALLBACK_TEMPLATES.research;
        return [
          { label: "Top buyer complaint", example: fb[0], color: "text-red-400" },
          { label: "What stops the sale", example: fb[1], color: "text-amber-400" },
          { label: "Buyer wording you can reuse", example: fb[2], color: "text-sky-400" },
          { label: "Copy angle", example: fb[3], color: "text-teal-400" },
        ];
      })();

  const handleClose = () => {
    phCapture("onboarding_skipped", { step });
    setStep(0);
    try { localStorage.setItem("scopsift_onboarding_v2_dismissed", "1"); } catch {}
    onClose();
  };

  const handleGoalSelect = (goalId: string) => {
    setSelectedGoal(goalId);
    if (goalError) setGoalError("");
    try {
      localStorage.setItem("scopsift_onboarding_v2_goal", goalId);
    } catch {}
  };

  const handleProceedToPreview = () => {
    const trimmed = productName.trim();
    let hasError = false;
    if (!trimmed) {
      setProductError("Please enter a product name.");
      hasError = true;
    } else {
      setProductError("");
    }
    if (!selectedGoal) {
      setGoalError("Please pick a goal.");
      hasError = true;
    } else {
      setGoalError("");
    }
    if (hasError) return;
    try {
      localStorage.setItem("scopsift_onboarding_v2_product", trimmed);
      localStorage.setItem("scopsift_onboarding_v2_goal", selectedGoal || "");
    } catch {}
    phCapture("onboarding_preview_viewed", {
      goal_selected: selectedGoal,
      product_name_present: !!trimmed,
    });
    setStep(2);
  };

  const handleComplete = () => {
    const trimmed = productName.trim();
    phCapture("onboarding_completed", {
      goal_selected: selectedGoal,
      product_name_present: !!trimmed,
    });
    try {
      localStorage.setItem("scopsift_onboarding_v2_completed", "1");
    } catch {}
    setStep(0);
    onClose(trimmed);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="dark-surface border-white/10 max-w-md max-h-[85vh] overflow-y-auto p-0 gap-0 [&>button]:text-white/60 [&>button]:hover:text-white">
        {/* Step indicator */}
        <div className="px-6 pt-6 pb-2">
          <div className="flex gap-1.5 mb-5">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className={`h-1 flex-1 rounded-full transition-colors ${
                  i <= step ? "bg-teal-400" : "bg-white/10"
                }`}
              />
            ))}
          </div>
        </div>

        {/* Screen 1 — Educate */}
        {step === 0 && (
          <div className="px-6 pb-6 animate-fade-in">
            <h2 className="text-xl font-bold text-white font-['Space_Grotesk'] leading-tight mb-5">
              Turn buyer chatter into copy&nbsp;that&nbsp;sells
            </h2>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Search className="h-5 w-5 text-teal-400 mt-0.5 shrink-0" />
                <p className="text-sm text-slate-300">Find what people <strong className="text-white">complain about</strong> (fast)</p>
              </div>
              <div className="flex items-start gap-3">
                <ShieldX className="h-5 w-5 text-teal-400 mt-0.5 shrink-0" />
                <p className="text-sm text-slate-300">Find what <strong className="text-white">stops them buying</strong></p>
              </div>
              <div className="flex items-start gap-3">
                <MessageSquareQuote className="h-5 w-5 text-teal-400 mt-0.5 shrink-0" />
                <p className="text-sm text-slate-300">Use <strong className="text-white">real buyer phrases</strong> so your copy sounds natural</p>
              </div>
            </div>
            <p className="mt-4 text-xs text-slate-400/80">Also useful for researching a product before you sell it.</p>

            <div className="mt-6 flex items-center gap-3">
              <div className="flex-1" />
              <Button
                variant="ghost"
                onClick={handleClose}
                className="text-white/50 hover:text-white hover:bg-white/10"
              >
                Skip
              </Button>
              <Button onClick={() => { phCapture("onboarding_started"); setStep(1); }} className="btn-gradient rounded-lg px-5">
                Next
              </Button>
            </div>
          </div>
        )}

        {/* Screen 2 — Personalise */}
        {step === 1 && (
          <div className="px-6 pb-6 animate-fade-in">
            <h2 className="text-xl font-bold text-white font-['Space_Grotesk'] leading-tight mb-2">
              What do you want to create today?
            </h2>
            <p className="text-xs text-slate-400 mb-5">Tell us what you're working on so we can tailor the outputs.</p>

            {/* Product name input */}
            <div className="space-y-1.5 mb-5">
              <Label className="text-slate-200 text-sm">Product name *</Label>
              <Input
                placeholder="e.g. yoga leggings, NAD+ supplement"
                value={productName}
                onChange={(e) => {
                  setProductName(e.target.value);
                  if (productError) setProductError("");
                }}
                className="dark-input border"
              />
              {productError && (
                <p className="text-xs text-destructive">{productError}</p>
              )}
            </div>

            {/* Goal cards */}
            <p className="text-xs text-slate-400 mb-3">Pick one — we'll tailor the outputs.</p>
            <div className="grid grid-cols-2 gap-3">
              {GOALS.map((goal) => {
                const Icon = goal.icon;
                const isSelected = selectedGoal === goal.id;
                return (
                  <button
                    key={goal.id}
                    type="button"
                    onClick={() => handleGoalSelect(goal.id)}
                    className={`flex flex-col items-center gap-2 rounded-xl border px-3 py-4 text-center transition-all ${
                      isSelected
                        ? "border-teal-400 bg-teal-400/10 text-white"
                        : "border-white/10 bg-white/[0.03] text-slate-300 hover:border-white/20 hover:bg-white/[0.06]"
                    }`}
                  >
                    <Icon className={`h-5 w-5 ${isSelected ? "text-teal-400" : "text-slate-400"}`} />
                    <span className="text-xs font-medium leading-tight">{goal.label}</span>
                  </button>
                );
              })}
            </div>
            {goalError && (
              <p className="mt-2 text-xs text-destructive">{goalError}</p>
            )}

            {/* Admin debug */}
            {isAdmin && (
              <p className="mt-2 text-[10px] font-mono text-slate-500">
                Step: {step} | Product: {productName.length} chars | Error: {productError || goalError || "none"}
              </p>
            )}

            <div className="mt-6 flex items-center gap-3">
              <Button
                variant="ghost"
                onClick={() => setStep(0)}
                className="text-white/70 hover:text-white hover:bg-white/10"
              >
                Back
              </Button>
              <div className="flex-1" />
              <Button
                variant="ghost"
                onClick={handleClose}
                className="text-white/50 hover:text-white hover:bg-white/10"
              >
                Skip
              </Button>
              <Button
                onClick={handleProceedToPreview}
                className="btn-gradient rounded-lg px-5"
              >
                Show my preview
              </Button>
            </div>
          </div>
        )}

        {/* Screen 3 — Gotcha preview */}
        {step === 2 && (
          <div className="px-6 pb-6 animate-fade-in">
            <h2 className="text-xl font-bold text-white font-['Space_Grotesk'] leading-tight mb-5">
              Here's what you'll get for <span className="text-teal-400">{normalizeProductName(productName)}</span>
            </h2>

            <div className="grid grid-cols-2 gap-2.5">
              {previewCards.map((card) => (
                <div
                  key={card.label}
                  className="rounded-xl dark-surface-alt border border-white/10 p-3"
                >
                  <p className={`text-[10px] uppercase tracking-wider font-semibold mb-1.5 ${card.color}`}>
                    {card.label}
                  </p>
                  <p className="text-xs text-slate-300 leading-relaxed italic">
                    {card.example}
                  </p>
                </div>
              ))}
            </div>

            <p className="mt-3 text-[11px] text-slate-400/80 leading-relaxed">
              This is what ScopSift pulls from Reddit/YouTube/forums/reviews — then turns into copy you can paste into Amazon/Shopify/ads.
            </p>

            {/* Locked preview row */}
            <div className="mt-3 flex items-center gap-2.5 rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3 opacity-60">
              <Lock className="h-4 w-4 text-slate-500 shrink-0" />
              <p className="text-xs text-slate-400">
                Create a free account to unlock: Full copy pack • Evidence links • Market Radar • Price Radar
              </p>
            </div>

            <div className="mt-6 flex items-center gap-3">
              <Button
                variant="ghost"
                onClick={() => setStep(1)}
                className="text-white/70 hover:text-white hover:bg-white/10"
              >
                Back
              </Button>
              <div className="flex-1" />
              <Button
                variant="ghost"
                onClick={handleClose}
                className="text-white/50 hover:text-white hover:bg-white/10"
              >
                Skip
              </Button>
              {user ? (
                <Button onClick={handleComplete} className="btn-gradient rounded-lg px-5">
                  Run my first report
                </Button>
              ) : (
                <Button
                  onClick={() => {
                    phCapture("onboarding_signup_clicked", { goal_selected: selectedGoal });
                    onNavigateToAuth();
                  }}
                  className="btn-gradient rounded-lg px-5"
                >
                  Sign up free
                </Button>
              )}
            </div>

            {!user && (
              <p className="text-[11px] text-slate-500 text-center mt-3">
                You can browse the form first — a free account is needed to generate reports.
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingModal;
